/**
 * The dispute flow: approve (DAI fee) → openDispute on-chain.
 *
 * Shared by all four order-detail screens — user/custodian × deposit/withdraw —
 * which are otherwise near-identical.
 *
 * NOTE ON THE POST-CONFIRMATION EFFECT
 * ------------------------------------
 * Unlike the withdraw flow there is **no outbox effect here**. Opening a
 * dispute on-chain does not immediately notify the backend: the screen flips to
 * a form, and `openDispute` is only emitted once the user has written a
 * description and uploaded evidence ([UploadFiles.jsx:259]). That payload
 * cannot be synthesised on their behalf, so it is not something a background
 * retry could ever complete.
 *
 * What durability buys here is still significant. Opening a dispute costs real
 * DAI, and today a reload between confirmation and form submission loses every
 * trace of it — the screen comes back showing the ordinary order view as though
 * nothing happened. With the journal, `isOpenDisputeConfirmed` is restored, the
 * screen re-derives `disputeOpened`, and the user lands back on the form they
 * still need to complete.
 *
 * NOTE ON THE DISPUTE VERIFIER
 * ----------------------------
 * CORRECTED 2026-09-07. This block used to argue that the dispute step could
 * have no verifier, because `getDispute(disputeId)` needs an id that only
 * exists in the log of the transaction we are looking for, and because
 * `getRequest(requestId)` "returns an opaque `phase` whose enum is not defined
 * anywhere in this repo".
 *
 * The first half is still true. The second is not, and following it cost us the
 * bug this flow exists to prevent. The enum IS defined —
 * `FrdmStructs.sol:57`, `phase == 1` means in dispute — and the backend has
 * relied on exactly that since `polygon.service.ts:959`. So the step gets the
 * verifier it always could have had: one `eth_call` against `getRequest`.
 *
 * Recovery still resolves by transaction hash in the ordinary case; the
 * verifier is what answers when there is no hash to resolve.
 */

import { registerFlow } from "../flows";
import { approveSatisfied, stageAlreadyOpen } from "../verifiers";

export const OPEN_DISPUTE_FLOW = "openDispute";
export const OPEN_DISPUTE_STEPS = { APPROVE: "approve", DISPUTE: "dispute" };

/** `Request.phase` values — FrdmStructs.sol:57. */
export const REQUEST_PHASE_DISPUTE = 1;
export const REQUEST_PHASE_APPEAL = 2;

registerFlow({
  id: OPEN_DISPUTE_FLOW,
  steps: {
    [OPEN_DISPUTE_STEPS.APPROVE]: { verifyOnChain: approveSatisfied },
    [OPEN_DISPUTE_STEPS.DISPUTE]: {
      verifyOnChain: stageAlreadyOpen(REQUEST_PHASE_DISPUTE),
    },
  },
});
